/* eslint-disable no-console */
import {
  FilterType,
  FilterUnion,
  IDestinationEntity,
  IMigrationDestination,
  IPracticeMigration,
  ISourceEntityTypes,
  SourceEntityRecordMigrationStatus,
  SourceEntityRecordStatus,
  type ISourceEntity,
  type ISourceEntityHandler,
  type ISourceEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import { IDestinationMigrationJobState } from '@principle-theorem/principle-core/interfaces';
import {
  SourceEntityRecord_Key,
  getSourceEntityRecord,
  setModifiedSourceEntityRecord,
  upsertSourceEntityRecord,
} from '@principle-theorem/principle-db-connector';
import {
  PrimaryKey,
  WithPrimaryKey,
  serialise,
  toMoment,
  toTimestamp,
  type Timezone,
  type WithRef,
} from '@principle-theorem/shared';
import { DataConnect } from 'firebase/data-connect';
import { isEmpty, isString } from 'lodash';
import { Postgres } from './connection';

export class SourceEntityRecord {
  static async get<Source extends ISourceEntityTypes>(
    dataConnect: DataConnect,
    sourceRef: SourceEntityRecord_Key | PrimaryKey<ISourceEntityRecord>
  ): Promise<WithPrimaryKey<ISourceEntityRecord<Source>> | undefined> {
    try {
      const result = await getSourceEntityRecord(dataConnect, {
        key: isString(sourceRef) ? { id: sourceRef } : sourceRef,
      });
      const record = result.data.sourceEntityRecord;

      if (!record) {
        return;
      }

      return {
        ...record,
        lastSync: toTimestamp(toMoment(record.lastSync)),
        status: record.status as SourceEntityRecordStatus,
        filters: record.filters as FilterUnion<Source['filters']>[],
        migrationDestinations:
          record.migrationDestinations as IMigrationDestination[],
        data: record.data as Source['data'],
        modifiedData: record.modifiedData as Source['data'],
        translations: record.translations as Source['translations'],
        createdAt: toTimestamp(toMoment(record.createdAt)),
        updatedAt: toTimestamp(toMoment(record.updatedAt)),
      };
    } catch (error) {
      console.error('SourceEntityRecord.get', error);
      throw error;
    }
  }

  static async upsertAdmin(
    migration: WithRef<IPracticeMigration>,
    sourceRecords: (ISourceEntityRecord &
      Partial<WithPrimaryKey<ISourceEntityRecord>>)[],
    sourceType: string
  ): Promise<void> {
    await Postgres.upsert(
      migration,
      'source_entity_record',
      sourceRecords.map((sourceRecord) => ({
        ...sourceRecord,
        // id: sourceRecord.id,
        uid: sourceRecord.uid,
        label: sourceRecord.label,
        status: sourceRecord.status,
        filters: sourceRecord.filters,
        migrationDestinations: sourceRecord.migrationDestinations ?? [],
        sourceType: sourceType,
        data: sourceRecord.data,
        modifiedData: sourceRecord.modifiedData ?? {},
        translations: sourceRecord.translations ?? {},
        lastSync: toTimestamp(),
        updatedAt: toTimestamp(),
      }))
    );
  }

  static async upsert(
    dataConnect: DataConnect,
    sourceRecord: ISourceEntityRecord &
      Partial<WithPrimaryKey<ISourceEntityRecord>>,
    sourceType: string
  ): Promise<void> {
    try {
      await upsertSourceEntityRecord(
        dataConnect,
        serialise({
          id: sourceRecord.id,
          uid: sourceRecord.uid,
          label: sourceRecord.label,
          status: sourceRecord.status,
          filters: sourceRecord.filters,
          migrationDestinations: sourceRecord.migrationDestinations ?? [],
          sourceType: sourceType,
          data: sourceRecord.data,
          modifiedData: sourceRecord.modifiedData ?? {},
          translations: sourceRecord.translations ?? {},
        })
      );
    } catch (error) {
      console.error('SourceEntityRecord.upsert', error);
      throw error;
    }
  }

  static async resetModifiedData(
    dataConnect: DataConnect,
    record: WithPrimaryKey<ISourceEntityRecord>
  ): Promise<void> {
    try {
      await setModifiedSourceEntityRecord(dataConnect, {
        id: record.id,
        modifiedData: {},
      });
    } catch (error) {
      console.error('SourceEntityRecord.resetModifiedData', error);
      throw error;
    }
  }

  static getLatestData<T = unknown>(
    record: WithPrimaryKey<ISourceEntityRecord>
  ): T {
    if (record.modifiedData && !isEmpty(record.modifiedData)) {
      return record.modifiedData as T;
    }
    return record.data as T;
  }

  static determineUidForRecord(
    rawDataId: string | number,
    sourceEntity: ISourceEntity
  ): string {
    return [sourceEntity.metadata.idPrefix, String(rawDataId)].join('-');
  }

  static buildRecord(
    sourceEntityHandler: ISourceEntityHandler,
    record: object,
    sourceEntity: WithRef<ISourceEntity>,
    timezone: Timezone,
    data: object
  ): ISourceEntityRecord {
    const uid = SourceEntityRecord.determineUidForRecord(
      sourceEntityHandler.getSourceRecordId(record),
      sourceEntity
    );
    return {
      uid,
      label: sourceEntityHandler.getSourceLabel(record),
      filters: sourceEntityHandler.getFilterData
        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          Object.entries(sourceEntityHandler.getFilterData(record, timezone))
            .map(([key, value]) => ({
              key,
              value,
            }))
            .flat()
        : [],
      lastSync: toTimestamp(),
      status: SourceEntityRecord.determineStatus(sourceEntityHandler, record),
      migrationDestinations: (
        sourceEntityHandler.migrationDestinations ?? []
      ).reduce(
        (destinations, destinationKey) => [
          ...destinations,
          {
            key: destinationKey,
            value: SourceEntityRecordMigrationStatus.NotMigrated,
          },
        ],
        [] as IMigrationDestination[]
      ),
      translations: sourceEntityHandler.translate(record, timezone),
      data,
    };
  }

  static determineStatus(
    sourceEntityHandler: ISourceEntityHandler,
    data: unknown
  ): SourceEntityRecordStatus {
    return sourceEntityHandler.verifySource(data, false)
      ? SourceEntityRecordStatus.Valid
      : SourceEntityRecordStatus.Invalid;
  }

  static async setMigratedByDestination(
    migration: WithRef<IPracticeMigration>,
    destinationKey: string,
    sourceRef: PrimaryKey<ISourceEntityRecord>,
    status: SourceEntityRecordMigrationStatus = SourceEntityRecordMigrationStatus.NotMigrated
  ): Promise<void> {
    try {
      await Postgres.setMigratedByDestination(
        migration,
        destinationKey,
        sourceRef,
        status
      );
    } catch (error) {
      console.error('SourceEntityRecord.setMigratedByDestination', error);
      throw error;
    }
  }

  static filterAccessor<
    Filters extends FilterType,
    Data extends object,
    Translations,
    K extends keyof Filters,
  >(
    data: ISourceEntityRecord<ISourceEntityTypes<Data, Translations, Filters>>,
    filterKey: K
  ): Filters[K] {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return data.filters.find((filter) => filter.key === filterKey)
      ?.value as Filters[K];
  }
}

export function buildFilterMigratedQuery(
  runType: IDestinationMigrationJobState['type'],
  destinationEntity: IDestinationEntity
): IMigrationDestination | undefined {
  if (runType === 'retryFailed') {
    return {
      key: destinationEntity.metadata.key,
      value: SourceEntityRecordMigrationStatus.Failed,
    };
  }

  if (runType === 'skipMigrated') {
    return {
      key: destinationEntity.metadata.key,
      value: SourceEntityRecordMigrationStatus.NotMigrated,
    };
  }

  if (runType === 'retryMigrated') {
    return {
      key: destinationEntity.metadata.key,
      value: SourceEntityRecordMigrationStatus.Migrated,
    };
  }
}
